class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    // Verified code hits per term. The dropdown re-renders on every keystroke and
    // on cache hits, which wipes anything injected; keeping the confirmed result
    // lets the next render put it straight back without asking the network again.
    this.codeHitsCache = {};

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    // Start the code lookup now rather than after the dropdown has rendered. It
    // does not depend on that response, and running the two in sequence is what
    // made the right product appear about a second after the wrong ones.
    this.startCodeLookup(searchTerm);

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
    this.recoverCodeSearch();
  }

  /*
    Storefront search tokenises variant SKUs, so a code stored as "FN 2500 M" is
    three tokens and a shopper typing "FN2500M" matches none of them — the dropdown
    comes back with nothing at all. Nothing here can change the index, so this
    changes the query: split the typed code at every position, ask predictive search
    for each, then confirm each candidate by reading its real SKUs. Only exact
    normalised matches are shown, so a wrong product can never surface; if nothing
    confirms, the dropdown is left exactly as it was.

    Three codes in this catalogue belong to one product spaced and a different
    product unspaced (33 201/202/203 versus 33201/33202/33203). Those unspaced forms
    are real SKUs, so Shopify finds them itself and this never runs for them. Where
    several products do share a normalised code, every one of them is listed rather
    than one being picked.
  */
  /* Kicks the lookup off for a term without touching the DOM, so it can run while
     the dropdown itself is still loading. Returns a promise of verified hits. */
  startCodeLookup(term) {
    if (!window.fetch || !window.Promise) return null;
    if (!this.isCodeQuery(term)) return null;
    if (this.codeHitsCache[term]) return Promise.resolve(this.codeHitsCache[term]);
    if (!this.codeLookups) this.codeLookups = {};
    if (this.codeLookups[term]) return this.codeLookups[term];

    // The input is debounced by 300ms, but a pause mid-code still starts a lookup
    // for a partial term ("33 00"). Drop those the moment a longer one begins,
    // rather than letting a dozen useless requests finish in the background.
    this.abortStaleCodeLookups(term);

    const controller =
      typeof AbortController === "function" ? new AbortController() : null;
    if (!this.codeControllers) this.codeControllers = {};
    this.codeControllers[term] = controller;

    const lookup = this.lookupCode(term, controller)
      .then((hits) => {
        this.codeHitsCache[term] = hits;
        return hits;
      })
      .catch(() => {
        // Aborted or failed: forget it so a later retype tries again.
        delete this.codeLookups[term];
        return [];
      });
    this.codeLookups[term] = lookup;
    return lookup;
  }

  abortStaleCodeLookups(keep) {
    if (!this.codeControllers) return;
    Object.keys(this.codeControllers).forEach((term) => {
      if (term === keep) return;
      const controller = this.codeControllers[term];
      if (controller) controller.abort();
      delete this.codeControllers[term];
      delete this.codeLookups[term];
    });
  }

  isCodeQuery(term) {
    return (
      /^[0-9A-Za-z][0-9A-Za-z \-\/#.,"]{2,30}$/.test(term) && /[0-9]/.test(term)
    );
  }

  recoverCodeSearch() {
    const term = this.getQuery();
    if (!window.fetch || !window.Promise) return;
    if (!this.isCodeQuery(term)) return;

    // predictive-search.liquid already reduced the list to exact SKU matches.
    if (this.querySelector('#predictive-search-results[data-sku-exact="true"]')) return;

    const hasSeparator = /[ \-\/#.,"]/.test(term);
    // An unseparated code that did find something was handled by normal search.
    if (
      !hasSeparator &&
      this.querySelector("#predictive-search-results-products-list li")
    ) {
      return;
    }

    const lookup = this.startCodeLookup(term);
    if (!lookup) return;
    lookup
      .then((hits) => {
        if (hits && hits.length && this.getQuery() === term) {
          this.renderCodeHits(hits);
        }
      })
      .catch(() => {
        // A failed recovery must never break the dropdown: leave it as it was.
      });
  }

  /*
    Storefront search tokenises variant SKUs, so a code stored as "FN 2500 M" is
    three tokens. Two shapes of query then fail, each needing a different net:

      "FN2500M"  — typed without separators, so nothing matches at all. Splitting
                   at every position guesses the stored form back, and the parts
                   on their own catch the rest.

      "33 003"   — typed exactly as stored, yet buried: "33" sits in 153 SKUs, so
                   86 products come back and the match ranks below the tenth,
                   which is as far as predictive search reaches. Asking for the
                   parts separately rescues it, because "003" sits in exactly one.

    Candidates are only leads. The SKU check decides, so a wrong product can never
    reach the shopper.
  */
  lookupCode(term, controller) {
    const normalise = (value) =>
      (value || "").toLowerCase().replace(/[ \-\/#.,"]/g, "");
    const code = normalise(term);
    const literal = term.toLowerCase().trim();
    const hasSeparator = /[ \-\/#.,"]/.test(term);

    let probes;
    if (hasSeparator) {
      const parts = term.split(/[ \-\/#.,"]+/).filter(Boolean);
      probes = parts.filter((part) => part.length >= 2).slice(0, 6);
      if (!probes.length) probes = parts.slice(0, 6);
      // Finds nothing today — that is the whole problem — but it is the probe that
      // hits directly once a normalised code lives in a searchable metafield, and
      // then none of this needs changing.
      if (code && probes.indexOf(code) === -1) probes.push(code);
    } else {
      const splits = [];
      const parts = {};
      for (let i = 1; i < term.length; i++) {
        splits.push(term.slice(0, i) + " " + term.slice(i));
        const head = term.slice(0, i);
        const tail = term.slice(i);
        if (head.length >= 2) parts[head] = true;
        if (tail.length >= 2) parts[tail] = true;
      }
      probes = splits.concat(
        Object.keys(parts).sort((a, b) => b.length - a.length)
      );
    }
    probes = probes.slice(0, 16);
    if (!probes.length) return Promise.resolve([]);

    const signal = controller ? controller.signal : undefined;
    const json = (url) =>
      fetch(url, signal ? { signal } : undefined)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);

    const verify = (candidate) =>
      json(`/products/${candidate.handle}.js`).then((full) => {
        if (!full || !full.variants) return null;
        // Literal beats normalised, matching predictive-search.liquid.
        const exact = full.variants.find(
          (item) => (item.sku || "").toLowerCase().trim() === literal
        );
        const variant =
          exact || full.variants.find((item) => normalise(item.sku) === code);
        return variant
          ? { product: candidate, variant, literal: Boolean(exact) }
          : null;
      });

    const pick = (checked) => {
      const hits = checked.filter(Boolean);
      const literalHits = hits.filter((hit) => hit.literal);
      return literalHits.length ? literalHits : hits;
    };

    return Promise.all(
      probes.map((query) =>
        json(
          `/search/suggest.json?q=${encodeURIComponent(
            query
          )}&resources%5Btype%5D=product&resources%5Blimit%5D=10`
        ).then((data) => {
          try {
            return data.resources.results.products || [];
          } catch (error) {
            return [];
          }
        })
      )
    ).then((lists) => {
      // Rank candidates by how narrow the probe that found them was. A probe
      // returning two products is far better evidence than one returning ten, and
      // checking those first means the answer usually arrives after four fetches
      // instead of fourteen.
      const best = {};
      lists.forEach((products) => {
        products.forEach((product, rank) => {
          if (!product || !product.handle) return;
          const score = products.length * 100 + rank;
          if (!(product.handle in best) || score < best[product.handle].score) {
            best[product.handle] = { product, score };
          }
        });
      });

      const candidates = Object.keys(best)
        .map((handle) => best[handle])
        .sort((a, b) => a.score - b.score)
        .map((entry) => entry.product)
        .slice(0, 14);

      if (!candidates.length) return [];

      const firstPass = candidates.slice(0, 4);
      const rest = candidates.slice(4);

      return Promise.all(firstPass.map(verify)).then((checked) => {
        const hits = pick(checked);
        if (!rest.length) return hits;
        // Keep checking the weaker candidates so a shared code still lists every
        // product that owns it, but do not make the shopper wait for them.
        const tail = Promise.all(rest.map(verify)).then((more) =>
          pick(checked.concat(more))
        );
        if (hits.length) {
          tail.then((all) => {
            if (all.length > hits.length) {
              this.codeHitsCache[term] = all;
              if (this.getQuery() === term) this.renderCodeHits(all, true);
            }
          });
          return hits;
        }
        return tail;
      });
    });
  }

  renderCodeHits(hits, replace) {
    const results = this.querySelector("#predictive-search-results");
    if (!results) return;
    const existing = results.querySelector("[data-code-hits]");
    if (existing) {
      // The slower candidates finished and found more products sharing this code.
      if (!replace) return;
      existing.remove();
    }

    const wrapper = document.createElement("div");
    wrapper.setAttribute("data-code-hits", "");
    wrapper.className = "predictive-search__result-group";

    const list = document.createElement("ul");
    list.className = "predictive-search__results-list list-unstyled";
    list.setAttribute("role", "group");

    hits.forEach((hit, index) => {
      const item = document.createElement("li");
      item.className = "predictive-search__list-item";
      item.setAttribute("role", "option");
      item.setAttribute("aria-selected", "false");
      item.id = `predictive-search-option-code-${index + 1}`;

      const link = document.createElement("a");
      link.className =
        "predictive-search__item predictive-search__item--link-with-thumbnail link link--text";
      link.setAttribute("tabindex", "-1");
      link.href = `/products/${hit.product.handle}${
        hit.variant && hit.variant.id ? `?variant=${hit.variant.id}` : ""
      }`;

      // featured_image is an object here while `image` is a plain string; using the
      // object directly renders "[object Object]" and a broken thumbnail.
      const featured = hit.product.featured_image;
      const thumb =
        (featured && typeof featured === "object" ? featured.url : featured) ||
        hit.product.image;
      if (thumb) {
        const image = document.createElement("img");
        image.className = "predictive-search__image";
        image.src = thumb;
        image.alt = "";
        image.width = 50;
        image.height = 50;
        link.appendChild(image);
      }

      const content = document.createElement("div");
      content.className = "predictive-search__item-content";

      const heading = document.createElement("p");
      heading.className = "predictive-search__item-heading";
      heading.textContent = hit.product.title;
      content.appendChild(heading);

      const sku = document.createElement("p");
      sku.className = "predictive-search__item-vendor caption";
      sku.textContent = `Kód: ${hit.variant.sku || ""}`;
      content.appendChild(sku);

      link.appendChild(content);
      item.appendChild(link);
      list.appendChild(item);
    });

    wrapper.appendChild(list);
    results.insertBefore(wrapper, results.firstChild);

    // The confirmed product answers the question, so drop the ranked list under it.
    // Those are products that merely share a token with the code — the same crowd
    // predictive-search.liquid removes when it can match on its own. Leaving them
    // would make the same search behave differently depending on which of the two
    // paths happened to find the answer.
    const ranked = this.querySelector("#predictive-search-results-products-list");
    if (ranked) {
      const group = ranked.closest(".predictive-search__result-group") || ranked;
      if (group !== wrapper) group.hidden = true;
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document
        .querySelector("#predictive__search_overlay")
        .getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
